import { lazy, Suspense } from 'react';

import type { ListProps } from 'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/types';
import EmptyDynamicState from 'hb-builder/components/common/state/EmptyDynamicState';
import ErrorState from 'hb-builder/components/common/state/ErrorState';
import SuspenseFallback from 'hb-builder/components/common/SuspenseFallback';
import { useGetDynamicSearch } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicSearch';
import { APP_TYPE } from 'hb-react/types/appType';

import { ListProvider } from './contexts/ListContext';

const SelectedLayout = {
  events: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/EventsWrapper'
      ),
  ),
  news: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/NewsWrapper'
      ),
  ),
  topics: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/List/ListLayouts/TopicsWrapper'
      ),
  ),
};

const ListLayouts = ({
  app,
  dynamicAttributes,
  ids,
  isPublic,
  item,
  navigate,
  networkRootUrl,
  type,
  whiteLabelUrl,
}: ListProps) => {
  const isBackOffice = app === APP_TYPE.BACKOFFICE;
  const params = {
    query: {
      page_visibility: isPublic ? 'public' : 'private',
      page: 1,
      per_page: dynamicAttributes?.query.numberOfItems,
      total_items: dynamicAttributes?.query.numberOfItems,
      type: dynamicAttributes?.query.dataType,
      categories: dynamicAttributes?.query.categories,
    },
  };

  const { data, error, isError, isLoading } = useGetDynamicSearch({
    app,
    params,
    type,
    whiteLabelUrl,
  });

  if (isError) {
    return isBackOffice ? <ErrorState error={error} /> : null;
  }

  if (data?.length === 0 && !isLoading) {
    return isBackOffice ? (
      <EmptyDynamicState component="list" type={type} />
    ) : null;
  }
  const Layout = SelectedLayout[type];

  const defaultProps = {
    ids,
    isBackOffice,
    isLoading,
    item,
    navigate,
    networkRootUrl,
    [type]: data,
    whiteLabelUrl,
  };

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <ListProvider>
        <Layout {...defaultProps} />
      </ListProvider>
    </Suspense>
  );
};

export default ListLayouts;
